import React, { useState } from 'react'
import styled from 'styled-components'
import { media } from 'utils/media'
import { usePathParam } from 'utils/usePathParam'
import { Button as OriginalActionButton } from 'lib/Button/Button'
import { LoadingDots } from 'lib/LoadingDots'
import { ModalPopup } from 'lib/ModalPopup'
import { Text } from 'lib/Text'
import { useCurrentPageBelongsToMe } from 'modules/auth/useCurrentPageBelongsToMe'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { ReviewImageModal } from 'modules/reviews/common/components/ReviewImageModal'
import { useUserReviews } from 'modules/reviews/list/queries/useUserReviews'
import { NoReviewsByUser } from '../../reviews/list/components/NoReviews'
import { ReviewItem } from '../../reviews/list/components/ReviewItem'
import { ReviewCounts } from './components/ReviewCounts'

const TopWrapper = styled.div`
  padding-bottom: 32px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--black-20);
`

const ActionButton = styled(OriginalActionButton)``

const Container = styled.article`
  margin-top: 44px;
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${ActionButton} {
    align-self: center;
    margin: 24px;
    min-width: 240px;
  }

  ${media.phoneDown} {
    margin-top: 32px;
  }
`

export const Reviews = () => {
  const { sessionIsInitialized } = useCurrentUser()
  const { currentPageBelongsToMe } = useCurrentPageBelongsToMe()
  const username = usePathParam('username') as string
  const { fetchMore, data, loading } = useUserReviews({ username, take: 10 })

  const [reviewImage, setReviewImage] = useState<string | null>(null)

  const handleModalClose = () => {
    setReviewImage(null)
  }

  if (loading || !data) {
    return (
      <Container>
        <TopWrapper>
          <Text as="h2" size="m" weight="semi">
            Reviews
          </Text>
        </TopWrapper>
        <LoadingDots fill />
      </Container>
    )
  }

  return (
    <Container>
      <TopWrapper>
        <Text as="h2" size="m" weight="semi" margin="2x">
          Reviews
        </Text>
        <ReviewCounts />
      </TopWrapper>

      {data.user?.reviews.items.length === 0 && <NoReviewsByUser isOwn={sessionIsInitialized && currentPageBelongsToMe} />}
      {data.user?.reviews.items.map((review) => (
        <ReviewItem showBrand showAuthor key={review.id} review={review} onClickImage={setReviewImage} />
      ))}
      {data.user?.reviews.hasMore && (
        <Container>
          <ActionButton
            disabled={loading}
            onClick={() => {
              fetchMore({ variables: { skip: data.user?.reviews.nextSkip } })
              window.analytics.track('Review Load More Clicked', { username })
            }}
            label="Load more reviews"
          />
        </Container>
      )}
      <ModalPopup
        open={reviewImage != null}
        title={null}
        onRequestClose={handleModalClose}
        showBorder={false}
        mobileFullscreen={false}
        size="large"
        variant="image">
        {reviewImage && <ReviewImageModal image={reviewImage} />}
      </ModalPopup>
    </Container>
  )
}
